// components/ModalScrollLock.jsx
'use client';

import { useEffect } from 'react';

/**
 * Freezes the page while any modal overlay is open.
 *
 * `overscroll-behavior: contain` (globals.css) is not enough on iOS: it only
 * governs gestures that START inside a scrollable element. A drag that begins
 * on a dialog's header, padding or buttons never enters a scroll container, so
 * WebKit hands it to the page behind the modal — the dialog sits still while
 * the background scrolls. Reproduced on the simulator 2026-08-13 with a probe
 * page: four drags on a dialog header moved the page from scrollY 592 to 1049
 * with containment fully in place, and moved it 0px with this body lock.
 *
 * The lock is the classic iOS pattern: `position: fixed` on <body> with the
 * current scroll offset as a negative top, restored on unlock. Plain
 * `overflow: hidden` is not used alone because iOS Safari has historically
 * ignored it for touch scrolling.
 *
 * WHY A GLOBAL WATCHER and not a hook in each modal: there are 62 locally
 * defined Modal components rendering ~88 overlays, all sharing one markup
 * convention — `fixed inset-0`. One observer keyed on that convention covers
 * every one of them, including modals added later, and cannot drift out of
 * sync the way 62 hand-threaded hooks would. The trade-off is that the
 * convention is now load-bearing: an overlay that drops those classes opts
 * out of the lock (and of the containment rule, which keys on the same pair).
 *
 * Mounted once in the root layout. Renders nothing.
 */
const OVERLAY_SELECTOR = '.fixed.inset-0';

function anyOverlayVisible() {
    for (const el of document.querySelectorAll(OVERLAY_SELECTOR)) {
        const cs = getComputedStyle(el);
        // The sidebar backdrops are mounted with `lg:hidden`; on desktop they
        // compute to display:none and must not lock anything.
        if (cs.display !== 'none' && cs.visibility !== 'hidden') return true;
    }
    return false;
}

export default function ModalScrollLock() {
    useEffect(() => {
        const body = document.body;
        let locked = false;
        let savedY = 0;

        function sync() {
            const shouldLock = anyOverlayVisible();
            if (shouldLock && !locked) {
                savedY = window.scrollY;
                body.style.position = 'fixed';
                body.style.top = `-${savedY}px`;
                body.style.left = '0';
                body.style.right = '0';
                body.style.width = '100%';
                body.style.overflow = 'hidden';
                locked = true;
            } else if (!shouldLock && locked) {
                body.style.position = '';
                body.style.top = '';
                body.style.left = '';
                body.style.right = '';
                body.style.width = '';
                body.style.overflow = '';
                locked = false;
                // 'instant', not default: html has scroll-behavior smooth, and a
                // smooth animation back to the saved offset reads as the page
                // lurching after every closed dialog.
                window.scrollTo({ top: savedY, behavior: 'instant' });
            }
        }

        // Modals mount and unmount; some toggle classes instead. Watch both.
        const mo = new MutationObserver(sync);
        mo.observe(body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style'],
        });
        sync();

        return () => {
            mo.disconnect();
            if (locked) {
                body.style.position = '';
                body.style.top = '';
                body.style.left = '';
                body.style.right = '';
                body.style.width = '';
                body.style.overflow = '';
                window.scrollTo({ top: savedY, behavior: 'instant' });
            }
        };
    }, []);

    return null;
}
